import type { QueryFunctionContext } from "./index";

type PollingQueryFn = (context: QueryFunctionContext) => Promise<unknown>;

export interface PollingCacheEntry {
  refetchTimer: ReturnType<typeof setInterval> | null;
  refetchIntervalMs: number | null;
  refetchIntervals: Map<number, number>;
  refetchQueryFn: PollingQueryFn | null;
}

export interface PollingQueryClient {
  _runFetch<TData>(
    k: string,
    queryFn: (context: QueryFunctionContext) => Promise<TData>,
    retryCount?: number,
  ): Promise<TData>;
}

export interface PollingQueryFnRef {
  current: PollingQueryFn;
}

export interface PollingRegistration {
  readonly qc: PollingQueryClient;
  readonly cacheKey: string;
  readonly entry: PollingCacheEntry;
  readonly interval: number;
  readonly queryFnRef: PollingQueryFnRef;
}

export function registerPollingInterval(
  input: PollingRegistration,
): () => void {
  input.entry.refetchQueryFn = (context) => input.queryFnRef.current(context);
  updateIntervalRefCount(input.entry, input.interval, 1);
  syncPollingTimer(input.qc, input.cacheKey, input.entry);
  return () => {
    updateIntervalRefCount(input.entry, input.interval, -1);
    syncPollingTimer(input.qc, input.cacheKey, input.entry);
  };
}

function nextPollingInterval(entry: PollingCacheEntry): number | null {
  const active = [...entry.refetchIntervals.entries()]
    .filter(([, count]) => count > 0)
    .map(([interval]) => interval);
  return active.length === 0 ? null : Math.min(...active);
}

function clearPollingTimer(entry: PollingCacheEntry): void {
  if (entry.refetchTimer) clearInterval(entry.refetchTimer);
  entry.refetchTimer = null;
  entry.refetchIntervalMs = null;
}

function syncPollingTimer(
  qc: PollingQueryClient,
  k: string,
  entry: PollingCacheEntry,
): void {
  const interval = nextPollingInterval(entry);
  if (interval === entry.refetchIntervalMs) return;
  clearPollingTimer(entry);
  if (interval === null) return;
  entry.refetchTimer = setInterval(() => {
    void qc._runFetch(k, entry.refetchQueryFn ?? noopQuery).catch(noop);
  }, interval);
  entry.refetchIntervalMs = interval;
}

function updateIntervalRefCount(
  entry: PollingCacheEntry,
  interval: number,
  delta: 1 | -1,
): void {
  const currentCount = entry.refetchIntervals.get(interval) ?? 0;
  const nextCount = Math.max(0, currentCount + delta);
  if (nextCount === 0) entry.refetchIntervals.delete(interval);
  else entry.refetchIntervals.set(interval, nextCount);
}

async function noopQuery(_context: QueryFunctionContext): Promise<unknown> {
  return undefined;
}

function noop(): void {
  return undefined;
}
